import type { PendingPaymentResolution } from '../types';

export interface PendingPaymentTracking {
  resolution: PendingPaymentResolution | '';
  continuedBy: string;
  continuedAtIso: string;
}

const RESOLUTIONS = new Set<PendingPaymentResolution>([
  'SWITCHED_TO_COD',
  'ONLINE_PAYMENT_CAPTURED',
]);

/** Continue is only valid while the order is still waiting on online payment. */
export function canContinuePendingPayment(status: string): boolean {
  return (status ?? '').trim().toUpperCase() === 'PENDING_PAYMENT';
}

export function parsePendingPaymentResolution(raw: string): PendingPaymentResolution | '' {
  const value = (raw ?? '').trim().toUpperCase();
  if (RESOLUTIONS.has(value as PendingPaymentResolution)) {
    return value as PendingPaymentResolution;
  }
  return '';
}

function firstString(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return '';
}

export function readPendingPaymentTracking(source: object): PendingPaymentTracking {
  const record = source as Record<string, unknown>;
  return {
    resolution: parsePendingPaymentResolution(
      firstString(record, 'pendingPaymentResolution', 'pending_payment_resolution'),
    ),
    continuedBy: firstString(record, 'pendingPaymentContinuedBy', 'pending_payment_continued_by'),
    continuedAtIso: firstString(
      record,
      'pendingPaymentContinuedAt',
      'pending_payment_continued_at',
    ),
  };
}

export function continuationNoteKey(
  resolution: string,
): 'continue.note.switchedToCod' | 'continue.note.onlineCaptured' | null {
  if (resolution === 'SWITCHED_TO_COD') return 'continue.note.switchedToCod';
  if (resolution === 'ONLINE_PAYMENT_CAPTURED') return 'continue.note.onlineCaptured';
  return null;
}

/** Short admin-id fallback when the API only sends an auth UUID. */
export function truncateAdminId(id: string): string {
  const value = id.trim();
  if (value.length <= 12) return value;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}
